import type { Components, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { elevation, radius } from '../palette';

/** A surface is a white sheet with one hairline. Elevation is kept for things that float over the page. */
export const surfaces: Components<Theme> = {
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: { root: ({ theme: t }) => ({ border: `1px solid ${t.palette.divider}`, borderRadius: radius.container, boxShadow: 'none', backgroundImage: 'none' }) },
  },
  MuiCard: { styleOverrides: { root: { '&:hover': { boxShadow: elevation.hover } } } },
  MuiPopover: { styleOverrides: { paper: { boxShadow: elevation.float, borderRadius: radius.container } } },
  MuiDialog: {
    styleOverrides: {
      paper: { boxShadow: elevation.modal, borderRadius: radius.container },
      root: ({ theme: t }) => ({ '& .MuiBackdrop-root': { backgroundColor: alpha(t.palette.primary.main, 0.32), backdropFilter: 'blur(2px)' } }),
    },
  },
  MuiDialogTitle: { styleOverrides: { root: { fontSize: '1.125rem', fontWeight: 600, letterSpacing: '-0.012em', paddingTop: 24, paddingBottom: 8 } } },
  MuiDialogContent: { styleOverrides: { root: { paddingBlock: 12 } } },
  MuiDialogActions: { styleOverrides: { root: ({ theme: t }) => ({ padding: '12px 24px 20px', gap: 4, borderTop: `1px solid ${t.palette.divider}`, marginTop: 8 }) } },
  MuiDrawer: { styleOverrides: { paper: ({ theme: t }) => ({ border: 'none', borderRadius: 0, boxShadow: elevation.modal, backgroundColor: t.palette.background.default }) } },
  MuiTooltip: {
    defaultProps: { arrow: true, enterDelay: 250 },
    styleOverrides: {
      tooltip: ({ theme: t }) => ({ borderRadius: radius.mark, fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.45, padding: '6px 10px', backgroundColor: t.palette.primary.main, maxWidth: 320 }),
      arrow: ({ theme: t }) => ({ color: t.palette.primary.main }),
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: radius.control, alignItems: 'flex-start', fontSize: '0.8125rem' },
      icon: { paddingTop: 8 },
      standard: ({ ownerState, theme: t }) => {
        const c = t.palette[ownerState.severity ?? 'info'];
        return { backgroundColor: alpha(c.main, 0.07), color: c.dark, border: `1px solid ${alpha(c.main, 0.2)}`, '& .MuiAlert-icon': { color: c.main } };
      },
      // Filled is only used for toasts: an ink slip with the status carried by the icon.
      filled: ({ ownerState, theme: t }) => ({ backgroundColor: t.palette.primary.main, color: t.palette.primary.contrastText, boxShadow: elevation.float, '& .MuiAlert-icon': { color: t.palette[ownerState.severity ?? 'info'].light } }),
    },
  },
  MuiSnackbar: { styleOverrides: { root: { '@media (max-width: 599.95px)': { bottom: 16 } } } },
  MuiCollapse: { defaultProps: { easing: { enter: 'cubic-bezier(0.2, 0, 0, 1)', exit: 'cubic-bezier(0.3, 0, 1, 1)' } } },
};
